import { NylasApiRequest } from '../../../common/nylas-api-request';
import { NylasSchedulerConfigStoreType } from '@/stores/scheduler-config-store';
import { dataResponse, debug, getCalendarRange } from '@/utils/utils';
import {
  Calendar,
  Configuration,
  EventDetails,
  GroupConfiguration,
  GroupEvent,
  GroupEventAPIData,
  ImportGroupEventDetails,
  ImportGroupEventResponse,
  NylasResponse,
  RecurrenceDeleteOption,
  RecurrenceUpdateOption,
  isNylasErrorResponse,
} from '@nylas/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

type NylasSchedulerConfigAPIConnectorOptions = {
  nylasApiRequest?: NylasApiRequest;
  nylasSchedulerConfigStore: NylasSchedulerConfigStoreType;
};

type GroupEventsCache = {
  data: GroupEventAPIData[];
  timestamp: number;
};

/**
 * Nylas Scheduler Config API Connector
 * This class is responsible for making API requests to the Nylas API for Scheduler Configurations.
 */
export class NylasSchedulerConfigAPIConnector {
  private nylasApiRequest?: NylasApiRequest;
  private nylasSchedulerConfigStore: NylasSchedulerConfigStoreType;
  private groupEventsCache: Map<string, GroupEventsCache> = new Map();
  private readonly CACHE_DURATION = 1 * 60 * 1000; //1 minute in milliseconds

  constructor({ nylasApiRequest, nylasSchedulerConfigStore }: NylasSchedulerConfigAPIConnectorOptions) {
    this.nylasApiRequest = nylasApiRequest;
    this.nylasSchedulerConfigStore = nylasSchedulerConfigStore;
  }

  /**
   * Get the current user.
   * @returns The current user or null if the request is not initialized.
   */
  public async currentUser() {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return null;
    }

    return this.nylasApiRequest.currentUser();
  }

  /**
   * Get the current user's calendars.
   * @returns The current user's calendars or an error if the request is not initialized.
   */
  public async getCalendars() {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error fetching calendars: Nylas API Request is not initialized',
      });
    }

    const allCalendars: Calendar[] = [];
    let cursor: string | null = null;
    const limit = 50;

    do {
      const [response, error] = await this.nylasApiRequest.request<NylasResponse<Calendar[]>>({
        method: 'GET',
        path: `calendars?limit=${limit}${cursor ? `&page_token=${cursor}` : ''}`,
      });

      if (error) {
        return dataResponse(null, {
          message: 'Error fetching calendars: ' + error.message,
        });
      }

      if (isNylasErrorResponse(response)) {
        return dataResponse(null, {
          message: 'Error fetching calendars: ' + response.error?.message,
        });
      }

      // Add the current page of calendars to our collection
      allCalendars.push(...response.data);

      // Update cursor for next iteration
      cursor = response.next_cursor || null;
    } while (cursor);

    // Set all calendars as the current calendars
    this.nylasSchedulerConfigStore.state.calendars = allCalendars;

    return dataResponse(allCalendars);
  }

  /**
   * Get a configuration by its ID.
   * @param configurationId The ID of the configuration to fetch.
   */
  public async getConfigurationById(configurationId: string, forceFetch: boolean = false) {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error fetching configuration: Nylas API Request is not initialized',
      });
    }

    // If the configuration is already in the store, return it
    if (!forceFetch) {
      const existingConfig = this.nylasSchedulerConfigStore.state.configurations.find(c => c.id === configurationId);
      if (existingConfig) {
        this.nylasSchedulerConfigStore.state.selectedConfiguration = existingConfig;
        return dataResponse(existingConfig);
      }
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<Configuration>>({
      method: 'GET',
      path: `scheduling/configurations/${configurationId}`,
    });

    if (error) {
      return dataResponse(null, {
        message: 'Error fetching configuration: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      return dataResponse(null, {
        message: 'Error fetching configuration: ' + response.error?.message,
      });
    }

    // Set the new configuration as the current configuration
    this.nylasSchedulerConfigStore.state.selectedConfiguration = response.data;

    // If the configuration was updated, update the list of configurations
    // If it doesn't exist, add it to the list
    if (this.nylasSchedulerConfigStore.state.configurations.find(c => c.id === configurationId)) {
      this.nylasSchedulerConfigStore.state.configurations = this.nylasSchedulerConfigStore.state.configurations.map(c => (c.id === configurationId ? response.data : c));
    } else {
      this.nylasSchedulerConfigStore.state.configurations = [...this.nylasSchedulerConfigStore.state.configurations, response.data];
    }

    return dataResponse(response.data);
  }

  /**
   * Get all configurations.
   * @returns The list of configurations or an error if the request is not initialized.
   */
  public async getConfigurations(cursor?: string) {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error fetching configurations: Nylas API Request is not initialized',
      });
    }
    const limit = 50;
    const [response, error] = await this.nylasApiRequest.request<NylasResponse<Configuration[]>>({
      method: 'GET',
      path: `scheduling/configurations?limit=${limit}${cursor ? `&page_token=${cursor}` : ''}`,
    });

    if (error) {
      return dataResponse(null, {
        message: 'Error fetching configurations: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      return dataResponse(null, {
        message: 'Error fetching configurations: ' + response.error?.message,
      });
    }

    // Set the configurations in the store
    this.nylasSchedulerConfigStore.state.configurations = response.data;
    this.nylasSchedulerConfigStore.state.listConfigurationsNextCursor = response.next_cursor || null;
    return dataResponse(response.data);
  }

  /**
   * Get master event by its ID.
   */
  public async getMasterEventById(masterEventId: string) {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error fetching master event: Nylas API Request is not initialized',
      });
    }

    const selectedGroupEvent = this.nylasSchedulerConfigStore.state.selectedGroupEvent;
    const calendar_id = selectedGroupEvent?.event?.calendar_id;
    if (!calendar_id) {
      debug('Calendar ID is not present');
      return dataResponse(null, {
        message: 'Error fetching master event: Calendar ID is not present',
      });
    }
    const queryParamsObject = new URLSearchParams();
    queryParamsObject.append('calendar_id', calendar_id);

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<EventDetails>>({
      method: 'GET',
      path: `events/${masterEventId}?${queryParamsObject.toString()}`,
    });

    if (error) {
      return dataResponse(null, {
        message: 'Error fetching master event: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      return dataResponse(null, {
        message: 'Error fetching master event: ' + response.error?.message,
      });
    }

    const masterGroupEvents = this.nylasSchedulerConfigStore.state.masterGroupEvents;
    // Set the new master event as the current master event
    if (masterGroupEvents[masterEventId]) {
      this.nylasSchedulerConfigStore.state.masterGroupEvents[masterEventId] = response.data;
    } else {
      this.nylasSchedulerConfigStore.state.masterGroupEvents = { ...masterGroupEvents, [masterEventId]: response.data };
    }

    return dataResponse(response.data);
  }

  /**
   * Create a new configuration.
   * @param configuration The configuration to create.
   * @returns The new configuration or an error if the request is not initialized.
   */
  public async createConfiguration(configuration: Omit<Partial<Configuration>, 'id'>) {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error creating configuration: Nylas API Request is not initialized',
      });
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<Configuration>>({
      method: 'POST',
      path: `scheduling/configurations`,
      body: configuration,
    });

    if (error) {
      return dataResponse(null, {
        message: 'Error creating configuration: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      return dataResponse(null, {
        message: 'Error creating configuration: ' + response.error?.message,
      });
    }

    // Set the new configuration as the current configuration
    this.nylasSchedulerConfigStore.state.selectedConfiguration = response.data;

    // Add the new configuration to the list of configurations
    this.nylasSchedulerConfigStore.state.configurations = [...this.nylasSchedulerConfigStore.state.configurations, response.data];

    return dataResponse(response.data);
  }

  /**
   * Update an existing configuration.
   * @param configuration The configuration to update.
   * @returns The updated configuration or an error if the request is not initialized.
   */
  public async updateConfiguration(configuration: Partial<Configuration>) {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error updating configuration: Nylas API Request is not initialized',
      });
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<Configuration>>({
      method: 'PUT',
      path: `scheduling/configurations/${configuration.id}`,
      body: configuration,
    });

    if (error) {
      return dataResponse(null, {
        message: 'Error updating configuration: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      return dataResponse(null, {
        message: 'Error updating configuration: ' + response.error?.message,
      });
    }

    // Set the new configuration as the current configuration
    this.nylasSchedulerConfigStore.state.selectedConfiguration = response.data;

    // If the configuration was updated, update the list of configurations
    // If it doesn't exist, add it to the list
    if (this.nylasSchedulerConfigStore.state.configurations.find(c => c.id === configuration.id)) {
      this.nylasSchedulerConfigStore.state.configurations = this.nylasSchedulerConfigStore.state.configurations.map(c => (c.id === configuration.id ? response.data : c));
    } else {
      this.nylasSchedulerConfigStore.state.configurations = [...this.nylasSchedulerConfigStore.state.configurations, response.data];
    }

    return dataResponse(response.data);
  }

  /**
   * Delete a configuration by its ID.
   * @param configurationId The ID of the configuration to delete.
   * @returns The deleted configuration or an error if the request is not initialized.
   */
  public async deleteConfiguration(configurationId: string) {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error deleting configuration: Nylas API Request is not initialized',
      });
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<Configuration>>({
      method: 'DELETE',
      path: `scheduling/configurations/${configurationId}`,
    });

    if (error) {
      return dataResponse(null, {
        message: 'Error deleting configuration: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      return dataResponse(null, {
        message: 'Error deleting configuration: ' + response.error?.message,
      });
    }

    // Remove the configuration from the list
    this.nylasSchedulerConfigStore.state.configurations = this.nylasSchedulerConfigStore.state.configurations.filter(c => c.id !== configurationId);

    // If the deleted configuration was the current configuration, reset it
    if (this.nylasSchedulerConfigStore.state.selectedConfiguration?.id === configurationId) {
      this.nylasSchedulerConfigStore.resetSelectedConfiguration();
    }

    return dataResponse(response.data);
  }

  /**
   * Create a group event.
   * @param eventDetails The group event to create.
   * @returns The group event or an error if the request is not initialized.
   */
  public async createGroupEvent(eventDetails: Omit<Partial<GroupEvent>, 'id'>, configurationId: string) {
    this.nylasSchedulerConfigStore.state.isLoading = true;

    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error creating group event: Nylas API Request is not initialized',
      });
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<GroupEventAPIData>>({
      method: 'POST',
      path: `scheduling/configurations/${configurationId}/group-events`,
      body: eventDetails,
    });

    if (error) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error creating group event: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error creating group event: ' + response.error?.message,
      });
    }

    // Set the new configuration as the current configuration
    this.nylasSchedulerConfigStore.state.selectedGroupEvent = response.data;

    // Invalidate cache so we fetch fresh data reflecting the new event
    this.invalidateGroupEventsCache();
    await this.getGroupEvents(configurationId, this.nylasSchedulerConfigStore.state.selectedConfiguration as Configuration | GroupConfiguration);

    this.nylasSchedulerConfigStore.state.isLoading = false;

    return dataResponse(response.data);
  }

  /**
   * Get a list of group events for a configuration.
   */
  public async getGroupEvents(configurationId: string, selectedConfiguration: Configuration | GroupConfiguration) {
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error fetching group events: Nylas API Request is not initialized',
      });
    }

    const queryParamsObject = new URLSearchParams();
    queryParamsObject.append('show_all', 'true');

    const calendarViewDateRange = this.nylasSchedulerConfigStore.state.calendarViewDateRange;
    let startTime: string;
    let endTime: string;

    if (calendarViewDateRange) {
      startTime = calendarViewDateRange.start_time;
      endTime = calendarViewDateRange.end_time;
    } else {
      const range = getCalendarRange(new Date().toISOString(), 'week');
      startTime = range.startTime.toString();
      endTime = range.endTime.toString();
    }

    // Create a cache key from the time range
    const cacheKey = `${configurationId}-${startTime}-${endTime}`;
    const cachedData = this.groupEventsCache.get(cacheKey);

    // Check if we have valid cached data
    if (cachedData && Date.now() - cachedData.timestamp < this.CACHE_DURATION) {
      this.nylasSchedulerConfigStore.state.groupEvents = cachedData.data;
      return dataResponse(cachedData.data);
    }

    queryParamsObject.append('start_time', startTime);
    queryParamsObject.append('end_time', endTime);

    const calendar_id = (selectedConfiguration as GroupConfiguration)?.group_booking?.calendar_id;
    if (calendar_id) {
      queryParamsObject.append('calendar_id', calendar_id);
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<GroupEventAPIData[]>>({
      method: 'GET',
      path: `scheduling/configurations/${configurationId}/group-events?${queryParamsObject.toString()}`,
    });

    if (error) {
      return dataResponse(null, {
        message: 'Error fetching group events: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      return dataResponse(null, {
        message: 'Error fetching group events: ' + response.error?.message,
      });
    }

    // Update cache with new data
    this.groupEventsCache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now(),
    });

    // Clean up expired cache entries
    this.cleanupExpiredCache();

    // Set the group events in the store
    this.nylasSchedulerConfigStore.state.groupEvents = response.data;

    return dataResponse(response.data);
  }

  /**
   * Invalidate all cached group events so the next getGroupEvents() call fetches fresh data.
   */
  private invalidateGroupEventsCache() {
    this.groupEventsCache.clear();
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.groupEventsCache.entries()) {
      if (now - value.timestamp > this.CACHE_DURATION) {
        this.groupEventsCache.delete(key);
      }
    }
  }

  /**
   * Update a group event.
   */
  public async updateGroupEventById(groupEventDetails: Partial<GroupEvent>, configurationId: string) {
    this.nylasSchedulerConfigStore.state.isLoading = true;
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error updating group event: Nylas API Request is not initialized',
      });
    }
    const groupEventId = groupEventDetails.id;
    delete groupEventDetails.id;

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<GroupEventAPIData>>({
      method: 'PATCH',
      path: `scheduling/configurations/${configurationId}/group-events/${groupEventId}`,
      body: groupEventDetails,
    });

    if (error) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error updating group event: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error updating group event: ' + response.error?.message,
      });
    }

    // Set the new group event as the current group event
    this.nylasSchedulerConfigStore.state.selectedGroupEvent = response.data;

    // Invalidate cache so we fetch fresh data reflecting the update
    this.invalidateGroupEventsCache();
    await this.getGroupEvents(configurationId, this.nylasSchedulerConfigStore.state.selectedConfiguration as Configuration | GroupConfiguration);

    this.nylasSchedulerConfigStore.state.isLoading = false;

    return dataResponse(response.data);
  }
  /**
   * Updates a group event based on recurrence logic.
   */
  public async updateGroupEvent(groupEventDetails: GroupEvent, chosenOption: RecurrenceUpdateOption, configurationId: string) {
    this.nylasSchedulerConfigStore.state.isLoading = true;
    const selectedGroupEvent = this.nylasSchedulerConfigStore.state.selectedGroupEvent;
    const masterEventId = selectedGroupEvent?.event?.master_event_id;
    const selectedGroupEventId = selectedGroupEvent?.event?.id;
    const selectedGroupEventStartTime = selectedGroupEvent?.event?.when?.start_time;
    const rruleString = selectedGroupEvent?.group_event_info?.rrule?.[0];
    const rruleStringWithoutUntil = rruleString
      ? rruleString
          .split(';')
          .filter(s => !s.startsWith('UNTIL'))
          .join(';')
      : '';
    const until = selectedGroupEventStartTime
      ? dayjs(selectedGroupEventStartTime * 1000)
          .subtract(1, 'minute')
          .format('YYYYMMDDTHHmmss[Z]')
      : '';
    if (chosenOption === 'this') {
      // Update only the current event (sub-event)
      return this.updateGroupEventById(
        {
          ...groupEventDetails,
          id: selectedGroupEventId,
          recurrence: masterEventId ? undefined : groupEventDetails.recurrence,
        },
        configurationId,
      );
    } else if (chosenOption === 'future' && masterEventId) {
      // 1️⃣ Create a new recurrence event with the same recurrence as before
      const newRecurrenceEvent = await this.createGroupEvent(groupEventDetails, configurationId);
      // 2️⃣ Update master event with UNTIL
      const updatedMasterEvent = {
        location: selectedGroupEvent?.event?.location,
        conferencing: selectedGroupEvent?.event?.conferencing,
        title: selectedGroupEvent?.event?.title,
        description: selectedGroupEvent?.event?.description,
        capacity: selectedGroupEvent?.group_event_info?.capacity,
        calendar_id: selectedGroupEvent?.event?.calendar_id,
        recurrence: rruleStringWithoutUntil ? [rruleStringWithoutUntil + ';UNTIL=' + until] : [`RRULE:FREQ=DAILY;UNTIL=${until}`],
      };
      await this.updateGroupEventById(
        {
          ...updatedMasterEvent,
          id: masterEventId,
        },
        configurationId,
      );
      return [updatedMasterEvent, newRecurrenceEvent];
    } else if (chosenOption === 'all') {
      // Update the master event
      // Check if the when is different
      const groupEventDetailsWhen = groupEventDetails?.when;
      const selectedGroupEventWhen = selectedGroupEvent?.event?.when;
      const masterEvent = this.nylasSchedulerConfigStore.state.masterGroupEvents?.[masterEventId as string];
      let when = masterEvent?.when;

      if (groupEventDetailsWhen?.start_time !== selectedGroupEventWhen?.start_time) {
        when = {
          ...when,
          start_time: groupEventDetailsWhen?.start_time || when?.start_time,
        };
      }
      if (groupEventDetailsWhen?.end_time !== selectedGroupEventWhen?.end_time) {
        when = {
          ...when,
          end_time: groupEventDetailsWhen?.end_time || when?.end_time,
        };
      }
      if (groupEventDetailsWhen?.start_timezone !== selectedGroupEventWhen?.start_timezone) {
        when = {
          ...when,
          start_timezone: groupEventDetailsWhen?.start_timezone || when?.start_timezone,
        };
      }
      if (groupEventDetailsWhen?.end_timezone !== selectedGroupEventWhen?.end_timezone) {
        when = {
          ...when,
          end_timezone: groupEventDetailsWhen?.end_timezone || when?.end_timezone,
        };
      }

      return this.updateGroupEventById(
        {
          ...groupEventDetails,
          when: when,
          id: masterEventId,
        },
        configurationId,
      );
    }

    this.nylasSchedulerConfigStore.state.isLoading = false;
    throw new Error('Invalid option');
  }

  /**
   * Delete a group event.
   */
  public async deleteGroupEvent(_groupEventDetails: GroupEvent, chosenOption: RecurrenceDeleteOption, configurationId: string) {
    this.nylasSchedulerConfigStore.state.isLoading = true;
    const selectedGroupEvent = this.nylasSchedulerConfigStore.state.selectedGroupEvent;
    const masterEventId = selectedGroupEvent?.event?.master_event_id;
    const selectedGroupEventId = selectedGroupEvent?.event?.id;
    const selectedGroupEventStartTime = selectedGroupEvent?.event?.when?.start_time;
    const rruleString = selectedGroupEvent?.group_event_info?.rrule?.[0];
    const rruleStringWithoutUntil = rruleString
      ? rruleString
          .split(';')
          .filter(s => !s.startsWith('UNTIL'))
          .join(';')
      : '';
    const until = selectedGroupEventStartTime ? dayjs(selectedGroupEventStartTime * 1000).format('YYYYMMDDTHHmmss[Z]') : '';
    const updatedMasterEvent = {
      location: selectedGroupEvent?.event?.location,
      conferencing: selectedGroupEvent?.event?.conferencing,
      title: selectedGroupEvent?.event?.title,
      description: selectedGroupEvent?.event?.description,
      capacity: selectedGroupEvent?.group_event_info?.capacity,
      calendar_id: selectedGroupEvent?.event?.calendar_id,
      recurrence: rruleStringWithoutUntil ? [rruleStringWithoutUntil + ';UNTIL=' + until] : [`RRULE:UNTIL=${until}`],
    };

    if (chosenOption === 'this' && selectedGroupEventId) {
      // Delete only the current event (sub-event)
      return this.deleteGroupEventById(selectedGroupEventId, configurationId);
    } else if (chosenOption === 'future' && masterEventId && selectedGroupEventId) {
      // Delete current event
      await this.deleteGroupEventById(selectedGroupEventId, configurationId);
      // Update master event with UNTIL
      return await this.updateGroupEventById(
        {
          ...updatedMasterEvent,
          id: masterEventId,
        },
        configurationId,
      );
    } else if (chosenOption === 'all' && masterEventId) {
      // Delete all events
      return this.deleteGroupEventById(masterEventId, configurationId);
    }

    this.nylasSchedulerConfigStore.state.isLoading = false;
    throw new Error('Invalid option');
  }

  /**
   * Delete a group event by its ID.
   */
  public async deleteGroupEventById(groupEventId: string, configurationId: string) {
    this.nylasSchedulerConfigStore.state.isLoading = true;
    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error deleting group event: Nylas API Request is not initialized',
      });
    }

    const queryParamsObject = new URLSearchParams();
    const calendar_id = (this.nylasSchedulerConfigStore.state.selectedConfiguration as GroupConfiguration)?.group_booking?.calendar_id;
    if (calendar_id) {
      queryParamsObject.append('calendar_id', calendar_id);
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<GroupEventAPIData>>({
      method: 'DELETE',
      path: `scheduling/configurations/${configurationId}/group-events/${groupEventId}?calendar_id=${calendar_id}`,
    });

    if (error) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error deleting group event: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error deleting group event: ' + response.error?.message,
      });
    }

    // Invalidate cache so we fetch fresh data reflecting the deletion
    this.invalidateGroupEventsCache();
    await this.getGroupEvents(configurationId, this.nylasSchedulerConfigStore.state.selectedConfiguration as Configuration | GroupConfiguration);
    this.nylasSchedulerConfigStore.state.isLoading = false;
    return dataResponse(response.data);
  }

  /**
   * Import a group event.
   */
  public async importGroupEvent(groupEventDetails: ImportGroupEventDetails[], configurationId: string) {
    this.nylasSchedulerConfigStore.state.isLoading = true;

    if (!this.nylasApiRequest) {
      debug('Nylas API Request is not initialized');
      return dataResponse(null, {
        message: 'Error importing group event: Nylas API Request is not initialized',
      });
    }

    const queryParamsObject = new URLSearchParams();
    const calendar_id = (this.nylasSchedulerConfigStore.state.selectedConfiguration as GroupConfiguration)?.group_booking?.calendar_id;
    if (calendar_id) {
      queryParamsObject.append('calendar_id', calendar_id);
    }

    const [response, error] = await this.nylasApiRequest.request<NylasResponse<ImportGroupEventResponse>>({
      method: 'POST',
      path: `scheduling/configurations/${configurationId}/import-group-events?${queryParamsObject.toString()}`,
      body: groupEventDetails,
    });

    if (error) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error importing group event: ' + error.message,
      });
    }

    if (isNylasErrorResponse(response)) {
      this.nylasSchedulerConfigStore.state.isLoading = false;
      return dataResponse(null, {
        message: 'Error importing group event: ' + response.error?.message,
      });
    }

    // Invalidate cache and refresh event list after import
    if (response.data?.imported_events?.length > 0) {
      this.invalidateGroupEventsCache();
      await this.getGroupEvents(configurationId, this.nylasSchedulerConfigStore.state.selectedConfiguration as Configuration | GroupConfiguration);
    }

    this.nylasSchedulerConfigStore.state.isLoading = false;

    return dataResponse(response.data);
  }
}
